import {FormFieldBase} from '@hconnect/common/types'
import {DateRangePicker} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import {
  parseTimeRangeParamToMomentRange,
  isSameDay
} from '@hconnect/uikit/src/lib2/components/momentDatePicker/dateRangePickerHelpers'
import {Moment} from 'moment-timezone'
import {Control, FieldValues, useController, Path} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useTimezoneByPlantId, useFormInputLabelWithSuffix} from '../../hooks'

type MomentDateRangePickerFormFieldProps<TField extends FieldValues> = Omit<
  FormFieldBase,
  'name'
> & {
  control: Control<TField>
  startName: Path<TField>
  endName: Path<TField>
  onChange?: (startDate: Moment | null, endDate: Moment | null) => void
  dataTestId?: string
  disableDateSwitch?: boolean
}

export const MomentDateRangePickerFormField = <TField extends FieldValues>({
  label,
  startName,
  endName,
  control,
  disabled,
  readOnly,
  disableDateSwitch,
  optional,
  onChange,
  dataTestId
}: MomentDateRangePickerFormFieldProps<TField>) => {
  const fieldDisabled = disabled || readOnly
  const startDateController = useController({name: startName, control, disabled: fieldDisabled})
  const endDateController = useController({name: endName, control, disabled: fieldDisabled})
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const {t} = useTranslation()
  const {timezone} = useTimezoneByPlantId()

  const isTimeRangeValid =
    startDateController.field.value !== undefined && endDateController.field.value !== undefined

  return (
    <DateRangePicker
      dataTestId={dataTestId}
      label={inputLabel}
      errorText={
        startDateController.fieldState.error?.message || endDateController.fieldState.error?.message
      }
      disabled={fieldDisabled}
      onTimeRangeParamChange={(value) => {
        const [newStartDate, newEndDate] = parseTimeRangeParamToMomentRange(value, timezone)
        const isSame = isSameDay([newStartDate, newEndDate])

        startDateController.field.onChange(isSame ? undefined : newStartDate)
        endDateController.field.onChange(newEndDate)

        if (onChange) {
          onChange(newStartDate, newEndDate)
        }
      }}
      timeRangeParam={[
        startDateController.field.value ?? endDateController.field.value ?? null,
        endDateController.field.value ?? null
      ]}
      useEndOfDayForSingleDate={false}
      singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
      timezone={timezone}
      noQuickSelection
      fullWidth
      isSingleDateDefault={!isTimeRangeValid}
      disableDateSwitch={disableDateSwitch}
      isContrastMode={false}
    />
  )
}
